import React, { Fragment, useContext, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl/dist';

// Components

import IncomeItem from '../../../../../../components/ssap/pages/pageIncludes/summary/IncomeItem';
import SubHeader from '../../../../../../components/ssap/headers/SubHeader';

// Contexts

import { Context } from '../../../../../../contexts/ssapSummaryContext';

// Hooks

import { useSummaryItemDiffRegister } from '../../../../../../hooks/useSummaryItemDiffRegister';

// Utils

import { currencyTools } from '../../../../../../utils/tools/currencyTools';
import { filterTools } from '../../../../../../utils/tools/filterTools';
import { getFrequencyId } from '../../../../../../utils/householdMember/getFrequencyId';
import { isEqual, pick } from '../../../../../../utils/miscellaneous/objectHelpers';

// Constants

import { FREQUENCY_TYPES } from '../../../../../../constants/ssap/hhmTypes/frequencyTypes';

const IncomesSummarySection = props =>{
    const {
        applicantGuid,
        constants: {
            NOT_APPLICABLE
        },
        incomes,
        isState,
        lists: {
            INCOME_SOURCES_LIST,
            FREQUENCIES_LIST
        },
        previousData,
        processDate,
        reportedIncome
    } = props;

    const summaryContext = useContext(Context);

    const {
        summaryItemsComparisonEnabled
    } = summaryContext;

    const getModifiedIncomes = (incomes, reportedIncome) => {
        let modifiedIncomes;

        const DEFAULT_FOR_PROJECTED_INCOME = [
            {
                amount: reportedIncome,
                cyclesPerFrequency: 0,
                frequency: FREQUENCY_TYPES.YEARLY,
                sourceName: null,
                type: NOT_APPLICABLE,
                incomeStartDate: null,
                incomeEndDate: null
            }
        ];

        switch(true) {
            case isState.NJ && incomes.length === 0:
                modifiedIncomes = DEFAULT_FOR_PROJECTED_INCOME;
                break;
            case incomes.length > 0:
                modifiedIncomes = incomes;
                break;
            default:
                modifiedIncomes = [];
        }

        return modifiedIncomes.filter(filterTools.removeDummyType);
    };

    const modifiedIncomes = useMemo(
        () => getModifiedIncomes(incomes, reportedIncome),
        [ incomes ]
    );

    const modifiedPreviousIncomes = useMemo(
        () => {
            const index = previousData.taxHousehold.householdMember.findIndex(hhm => hhm.applicantGuid === applicantGuid);
            if(~index) {
                const {
                    annualTaxIncome: {
                        reportedIncome
                    },
                    incomes
                } = previousData.taxHousehold.householdMember[index];
                return getModifiedIncomes(incomes, reportedIncome);
            }
            return [];
        },
        [ previousData ]
    );

    const [
        arePreviousIncomesShown,
        toggleIncomesVisibility
    ] = useState(!modifiedPreviousIncomes.length);

    const handleToggleLinkClick = e => {
        e.preventDefault();
        toggleIncomesVisibility(isShown => !isShown);
    };

    const filterIncomeProps = (incomes = []) => {
        return incomes.map(income => {
            return pick(
                income,
                [
                    'type',
                    'sourceName',
                    'amount',
                    'frequency',
                    'incomeStartDate',
                    'incomeEndDate'
                ]
            );
        });
    };

    const diffs = useMemo(
        () => {
            return {
                status: !isEqual(filterIncomeProps(modifiedIncomes), filterIncomeProps(modifiedPreviousIncomes))
            };
        },
        [ modifiedIncomes, modifiedPreviousIncomes ]
    );

    const toggleLink = (
        <a
            href = '#'
            onClick = { handleToggleLinkClick }
            className = 'instruction_link instruction_link_p'
        >
            <FormattedMessage id = 'ssap.summary.show_previous_values_link' />
        </a>
    );

    const linkProps =  (
        summaryItemsComparisonEnabled
        && diffs.status
        && modifiedPreviousIncomes.length
    )
        ? {
            auxText: 'common.non_internationalized_text',
            auxTextReplacements: { text: toggleLink }
        }
        : { };

    const getIncomes = (incomes = [], arePrevious = false) => {
        if(incomes.length === 0 && arePrevious) {
            return (
                <div className = 'grid-row' >
                    <FormattedMessage id = 'common.no_text' />
                </div>
            );
        }

        return incomes.map((income, idx) => {
            const {
                amount,
                cyclesPerFrequency,
                frequency,
                sourceName,
                type,
                incomeStartDate,
                incomeEndDate
            } = income;

            return (
                <IncomeItem
                    id = { type }
                    key = { `${ type }_${ idx }` }
                    type = { INCOME_SOURCES_LIST[type] }
                    sourceName = { sourceName }
                    amount = { currencyTools.getMoneyFormat(amount, true) }
                    frequency = { getFrequencyId(frequency) }
                    incomeStartDate = { processDate(incomeStartDate) }
                    incomeEndDate = { processDate(incomeEndDate) }
                    replacements = { {
                        frequency: FREQUENCIES_LIST[frequency],
                        qty: cyclesPerFrequency
                    } }
                    isStrikeThroughValue = { arePrevious }
                />
            );
        });
    };

    useSummaryItemDiffRegister({
        diffs: { status: modifiedIncomes.length === 0 ? false : diffs.status },
        summaryContext,
        values: {
            category: 'incomes',
            value: modifiedIncomes,
            previousValue: modifiedPreviousIncomes,
            textCode: applicantGuid
        }
    });

    if(modifiedIncomes.length === 0) {
        return null;
    }

    const displayUpdatedRow = arePreviousIncomesShown ? 'bg-updated_fields' : '';

    return (
        <Fragment>
            <SubHeader
                { ...{
                    text: 'ssap.subheader.current_income',
                    headerClassName: 'gi-heading-summary summary__header--no-border',
                    ...linkProps
                } }
            />
            <IncomeItem
                id = 'Income Source'
                type = 'common.income_source'
                amount = 'common.how_much_without_question_mark'
                frequency = 'common.how_often_without_question_mark'
                incomeStartDate = 'common.start_date'
                incomeEndDate = 'common.end_date'
                design = 'header'
            />
            <div className = { displayUpdatedRow } >
                {
                    arePreviousIncomesShown && getIncomes(modifiedPreviousIncomes, true)
                }
                {
                    getIncomes(modifiedIncomes)
                }
            </div>
        </Fragment>
    );
};

export default IncomesSummarySection;